import { lockScroll, unlockScroll } from '@theme/utilities';

const CLOSE_TRANSITION_DURATION = 450;

class DtfCampaign extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    this._initialized = true;
    this._designMode = Boolean(window.Shopify?.designMode);
    this.dialog = this.querySelector('[data-dialog]');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this._onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const opener = event.target.closest('[data-campaign-open]');
      if (opener && this.dataset.enabled === 'true') this.open(opener);
      else if (event.target.closest('[data-close]')) this.close();
    };
    this._onCancel = (event) => {
      event.preventDefault();
      this.close();
    };
    this._onDialogClick = (event) => {
      if (event.target !== this.dialog) return;

      const rect = this.dialog.getBoundingClientRect();
      const outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (outside) this.close();
    };
    this.addEventListener('click', this._onClick);
    this.dialog.addEventListener('cancel', this._onCancel);
    this.dialog.addEventListener('click', this._onDialogClick);

    if (this._designMode) {
      this._onSectionSelect = (event) => {
        if (event.target instanceof Element && event.target.contains(this)) this.open();
      };
      this._onSectionDeselect = (event) => {
        if (event.target instanceof Element && event.target.contains(this)) this.close();
      };
      document.addEventListener('shopify:section:select', this._onSectionSelect);
      document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
      return;
    }

    if (this.dataset.enabled !== 'true') return;

    if (this.querySelector('[data-posted], [data-errors]')) {
      this._timer = setTimeout(() => this.open(), 400);
      return;
    }

  }

  open(opener) {
    if (!this.isConnected || !this.dialog || this.dialog.open || this._closing) return;

    this._returnFocus = opener instanceof HTMLElement && opener.isConnected
      ? opener
      : document.activeElement instanceof HTMLElement ? document.activeElement : null;
    try {
      this.dialog.showModal();
    } catch (error) {
      return;
    }
    lockScroll(this.dialog);
    this._raf = requestAnimationFrame(() => {
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.classList.add('is-open');
      });
    });

    const feedback = this.querySelector('[data-errors], [data-posted]');
    if (feedback instanceof HTMLElement) {
      feedback.focus();
    } else {
      this.querySelector('.dtf-camp__x')?.focus();
    }
  }

  close() {
    if (!this.dialog?.open || this._closing) return;

    this._closing = true;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    this.classList.remove('is-open');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClose(true);
      return;
    }

    this._closeTimer = setTimeout(() => this.finishClose(true), CLOSE_TRANSITION_DURATION);
  }

  finishClose(restoreFocus) {
    clearTimeout(this._closeTimer);
    this._closeTimer = null;
    if (this.dialog?.open) this.dialog.close();
    if (this.dialog) unlockScroll(this.dialog);
    this._closing = false;

    if (restoreFocus && this._returnFocus?.isConnected) this._returnFocus.focus();
    this._returnFocus = null;
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    clearTimeout(this._closeTimer);
    if (this._raf) cancelAnimationFrame(this._raf);
    this.removeEventListener('click', this._onClick);
    this.dialog?.removeEventListener('cancel', this._onCancel);
    this.dialog?.removeEventListener('click', this._onDialogClick);
    document.removeEventListener('shopify:section:select', this._onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
    this.classList.remove('is-open');
    this.finishClose(false);
    this._initialized = false;
  }
}

if (!customElements.get('dtf-campaign')) {
  customElements.define('dtf-campaign', DtfCampaign);
}
